import { useQuery, UseQueryOptions, UseQueryResult } from 'react-query';
import { CustomerDeviceAssignment } from 'src/types/CustomerDeviceAssignment';
import { get, post } from '../utils';

enum QueryKeys {
  CUSTOMER_DEVICE_ASSIGNMENT = 'CustomerDeviceAssignment',
}

export function createCustomerDeviceAssignment(customerId: string, deviceId: string): Promise<CustomerDeviceAssignment> {
  const body = {
    customerId,
    deviceId,
  };
  return post<CustomerDeviceAssignment>('customerdeviceassignment', body);
}

export function getCustomerDeviceAssignments(): Promise<CustomerDeviceAssignment[]> {
  return get<CustomerDeviceAssignment[]>('customerdeviceassignment');
}

export function getCustomerDeviceAssignmentsForCustomer(customerId: string): Promise<CustomerDeviceAssignment[]> {
  return get<CustomerDeviceAssignment[]>('customerdeviceassignment', { customerId });
}

export function getCustomerDeviceAssignmentsForDevice(deviceId: string): Promise<CustomerDeviceAssignment[]> {
  return get<CustomerDeviceAssignment[]>('customerdeviceassignment', { deviceId });
}

/**
 * Ids per request.
 *
 * Set by the TRANSPORT, not by the server's cap (200) or by anything about the data. Ids
 * travel in the URL, and a microservice JWT is itself ~5.3KB of the 16KB of request head
 * Node accepts — measured against this API, 200 ids is a 9.5KB request line and answers
 * 200, while 250 is 11.8KB and is refused with 431 Request Header Fields Too Large.
 *
 * 100 leaves room for a longer token or an extra header rather than sitting just under a
 * ceiling that moves. It is 20 requests for a full 2,000-device browse, against 2,000 for
 * the per-device version it replaced.
 */
export const CUSTOMER_ASSIGNMENT_BATCH_SIZE = 100;

/**
 * Assignments for a LIST of devices, in batches.
 *
 * The device list needs the owning customer for every row it shows and was asking one
 * device at a time — on the browse-by-type view that is one request per device in the
 * result set, which on a common device type is tens of thousands from a single tab.
 */
export async function getCustomerDeviceAssignmentsForDevices(
  deviceIds: string[]
): Promise<CustomerDeviceAssignment[]> {
  const collected: CustomerDeviceAssignment[] = [];

  for (let i = 0; i < deviceIds.length; i += CUSTOMER_ASSIGNMENT_BATCH_SIZE) {
    const batch = deviceIds.slice(i, i + CUSTOMER_ASSIGNMENT_BATCH_SIZE);
    collected.push(...(await get<CustomerDeviceAssignment[]>('customerdeviceassignment', { deviceIds: batch })));
  }

  return collected;
}

export function useGetCustomerDeviceAssignmentsForCustomer(
  customerId: string,
  options?: UseQueryOptions<CustomerDeviceAssignment[], Error>
): UseQueryResult<CustomerDeviceAssignment[], Error> {
  return useQuery([QueryKeys.CUSTOMER_DEVICE_ASSIGNMENT, customerId], () => getCustomerDeviceAssignmentsForCustomer(customerId), options);
}
